// ════════════════════════════════════════════════════════════════════════════
//  DRILL-DOWN MODAL — click any chart on the admin dashboards and it opens
//  here. One generic renderer for the /admin/analytics/drilldown contract:
//  KPI strip · chart (area/bars/stacked) · ranked breakdown · real-record
//  table. Live: re-fetches every 12s while open + instant socket push.
// ════════════════════════════════════════════════════════════════════════════
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import api from '../../api/axios';
import { CHART, fmtINR, fmtNum, CosmicTooltip, StatusChip, useCountUp } from './kit';

const COLOR = { gold: CHART.gold, tulsi: CHART.tulsi, violet: CHART.violet, lotus: CHART.lotus };
const seriesColor = (s, i) => COLOR[s.color] || CHART.categorical[i % 4];

const KIND_CHIP = {
  Order: { icon: 'shopping_bag', c: CHART.violet },
  Donation: { icon: 'volunteer_activism', c: CHART.gold },
  Offering: { icon: 'local_florist', c: CHART.tulsi },
  PAID: { icon: 'verified', c: CHART.tulsi },
  PENDING: { icon: 'schedule', c: '#D96A88' },
};

const MiniKpi = ({ icon, label, value, money, suffix }) => {
  const v = useCountUp(value, 700);
  return (
    <div className="rounded-xl border border-[rgba(229,192,107,.14)] bg-white/[0.04] px-4 py-3">
      <p className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.16em] text-muted">
        <span className="material-symbols-outlined text-[14px] text-gold">{icon}</span>{label}
      </p>
      <p className="mt-1 font-display text-xl font-bold tabular-nums text-ivory">
        {money ? fmtINR(v) : fmtNum(Math.round(v))}{suffix || ''}
      </p>
    </div>
  );
};

const DrillChart = ({ chart }) => {
  if (!chart || !chart.data?.length) return null;
  const multi = chart.series.length > 1;
  const axisProps = {
    x: { dataKey: chart.xKey, tick: { fill: CHART.axis, fontSize: 10, fontWeight: 700 }, axisLine: false, tickLine: false },
    y: {
      tick: { fill: CHART.axis, fontSize: 10 }, axisLine: false, tickLine: false, width: 40,
      tickFormatter: (v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v),
    },
  };
  return (
    <div className="h-60">
      <ResponsiveContainer width="100%" height="100%">
        {chart.kind === 'area' ? (
          <AreaChart data={chart.data} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}>
            <defs>
              {chart.series.map((s, i) => (
                <linearGradient key={s.key} id={`dd-g-${i}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={seriesColor(s, i)} stopOpacity={0.42} />
                  <stop offset="100%" stopColor={seriesColor(s, i)} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis {...axisProps.x} interval="preserveStartEnd" />
            <YAxis {...axisProps.y} />
            <Tooltip content={<CosmicTooltip money={chart.money} />} cursor={{ stroke: 'rgba(246,220,150,.35)', strokeWidth: 1 }} />
            {multi && <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} iconType="plainline" />}
            {chart.series.map((s, i) => (
              <Area key={s.key} type="monotone" dataKey={s.key} name={s.key} stroke={seriesColor(s, i)}
                strokeWidth={2} fill={`url(#dd-g-${i})`} dot={false} activeDot={{ r: 4 }} />
            ))}
          </AreaChart>
        ) : (
          <BarChart data={chart.data} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}
            barSize={chart.data.length > 15 ? 10 : 22}>
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis {...axisProps.x} interval={chart.data.length > 15 ? 4 : 0} />
            <YAxis {...axisProps.y} />
            <Tooltip content={<CosmicTooltip money={chart.money} />} cursor={{ fill: 'rgba(246,220,150,.05)' }} />
            {multi && <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} />}
            {chart.series.map((s, i) => (
              <Bar key={s.key} dataKey={s.key} name={s.key} stackId={chart.kind === 'stacked' ? 'dd' : undefined}
                fill={seriesColor(s, i)} stroke="#150E2E" strokeWidth={chart.kind === 'stacked' ? 2 : 0}
                radius={chart.kind === 'stacked' && i === chart.series.length - 1 ? [4, 4, 0, 0] : chart.kind === 'stacked' ? 0 : [4, 4, 0, 0]} />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

const Cellv = ({ col, row }) => {
  const v = row[col.key];
  if (col.kind === 'money') return <span className="font-bold tabular-nums text-gold-light">{fmtINR(v)}</span>;
  if (col.kind === 'num') return <span className="font-bold tabular-nums text-ivory">{fmtNum(v)}</span>;
  if (col.kind === 'date') return <span className="text-[10px] font-semibold text-muted">{v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true }) : '—'}</span>;
  if (col.kind === 'status') return <StatusChip status={v} />;
  if (col.kind === 'chip') {
    const k = KIND_CHIP[v] || { icon: 'category', c: CHART.axis };
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest" style={{ color: k.c }}>
        <span className="material-symbols-outlined text-[12px]">{k.icon}</span>{v}
      </span>
    );
  }
  return <span className="font-semibold text-stone-300">{v ?? '—'}</span>;
};

const toCsv = (rows) => {
  if (!rows?.data?.length) return '';
  const cols = rows.columns;
  const esc = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  return [cols.map(c => esc(c.label)).join(','),
    ...rows.data.map(r => cols.map(c => esc(c.kind === 'date' && r[c.key] ? new Date(r[c.key]).toISOString() : r[c.key])).join(','))].join('\n');
};

const DrillDown = ({ drill, onClose }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const closingRef = useRef(false);

  const fetchIt = useCallback(async () => {
    if (!drill) return;
    try {
      const res = await api.get('/admin/analytics/drilldown', { params: { type: drill.type, key: drill.key } });
      setD(res.data?.data || null); setErr(null);
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not load drill-down');
    }
  }, [drill]);

  // fetch on open + poll every 12s + socket push while open
  useEffect(() => {
    if (!drill) return;
    setD(null); setErr(null);
    fetchIt();
    const id = setInterval(fetchIt, 12000);
    const socket = window.socket;
    const onEvent = () => fetchIt();
    socket?.on('analytics_event', onEvent);
    socket?.on('order_status_updated', onEvent);
    return () => { clearInterval(id); socket?.off('analytics_event', onEvent); socket?.off('order_status_updated', onEvent); };
  }, [drill, fetchIt]);

  // Esc closes
  useEffect(() => {
    if (!drill) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drill, onClose]);

  if (!drill) return null;

  const exportCsv = () => {
    const csv = toCsv(d?.rows);
    if (!csv) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `${(d?.title || 'drilldown').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center p-0 sm:items-center sm:p-6"
      onMouseDown={(e) => { closingRef.current = e.target === e.currentTarget; }}
      onMouseUp={(e) => { if (closingRef.current && e.target === e.currentTarget) onClose(); closingRef.current = false; }}>
      <div className="absolute inset-0 bg-[#08051475] backdrop-blur-md" style={{ animation: 'ddFade .25s ease both' }} />
      <div className="glass-card relative flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-3xl border border-[rgba(229,192,107,.22)] shadow-[0_40px_120px_rgba(0,0,0,.6)] sm:rounded-3xl"
        style={{ animation: 'ddRise .32s cubic-bezier(.22,1,.36,1) both', background: 'linear-gradient(165deg, rgba(26,17,51,.97), rgba(14,9,29,.98))' }}>

        {/* header */}
        <div className="flex items-start justify-between gap-4 border-b border-[rgba(229,192,107,.14)] px-6 py-5">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-[9px] font-black uppercase tracking-[0.24em] text-gold-deep">
              Drill-down · Live aggregation
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-aura opacity-60"></span>
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-aura"></span>
              </span>
            </p>
            <h2 className="mt-1 truncate font-display text-2xl font-bold text-ivory">{d?.title || '…'}</h2>
            {d?.subtitle && <p className="mt-0.5 text-[11px] font-semibold text-muted">{d.subtitle}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {d?.rows?.data?.length > 0 && (
              <button onClick={exportCsv} title="Export rows as CSV"
                className="flex items-center gap-1.5 rounded-xl border border-[rgba(229,192,107,.2)] bg-white/[0.04] px-3 py-2 text-[10px] font-black uppercase tracking-widest text-gold transition-colors hover:text-gold-light">
                <span className="material-symbols-outlined text-[15px]">download</span> CSV
              </button>
            )}
            <button onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-[rgba(229,192,107,.2)] bg-white/[0.04] text-stone-300 transition-colors hover:text-ivory">
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>
        </div>

        {/* body */}
        <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {err && (
            <p className="py-14 text-center text-[11px] font-bold uppercase tracking-widest text-red-400">{err}</p>
          )}
          {!d && !err && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {[0, 1, 2, 3].map(i => <div key={i} className="h-[74px] animate-pulse rounded-xl bg-white/[0.05]" />)}
              </div>
              <div className="h-60 animate-pulse rounded-xl bg-white/[0.05]" />
              <div className="h-40 animate-pulse rounded-xl bg-white/[0.05]" />
            </div>
          )}
          {d && (
            <>
              {/* KPI strip */}
              {d.kpis?.length > 0 && (
                <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                  {d.kpis.map(k => <MiniKpi key={k.label} {...k} />)}
                </div>
              )}

              <DrillChart chart={d.chart} />

              {/* ranked breakdown */}
              {d.breakdown?.items?.length > 0 && (
                <div className="mt-6">
                  <p className="mb-3 text-[9px] font-black uppercase tracking-[0.2em] text-muted">{d.breakdown.title}</p>
                  <div className="space-y-2.5">
                    {d.breakdown.items.map((b, i) => {
                      const max = d.breakdown.items[0]?.value || 1;
                      return (
                        <div key={b.name}>
                          <div className="mb-1 flex items-center justify-between gap-3 text-[11px] font-bold">
                            <span className="flex min-w-0 items-center gap-2 text-stone-300">
                              <span className="truncate">{i + 1}. {b.name}</span>
                              {b.live && <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-red-400" title="Streaming live" />}
                            </span>
                            <span className="shrink-0 tabular-nums text-gold-light">
                              {fmtINR(b.value)}{b.count != null && <span className="font-semibold text-muted"> · {fmtNum(b.count)} txn</span>}
                            </span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
                            <div className="h-full rounded-full transition-all duration-700"
                              style={{ width: `${Math.max(2, (b.value / max) * 100)}%`, background: `linear-gradient(90deg, ${CHART.categorical[i % 4]}, ${CHART.categorical[i % 4]}bb)` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* real-record table */}
              {d.rows?.data?.length > 0 && (
                <div className="mt-6">
                  <p className="mb-2 text-[9px] font-black uppercase tracking-[0.2em] text-muted">{d.rows.title}</p>
                  <div className="overflow-x-auto rounded-xl border border-[rgba(229,192,107,.10)]">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-[rgba(229,192,107,.14)] bg-white/[0.03] text-[9px] font-black uppercase tracking-[0.16em] text-muted">
                          {d.rows.columns.map(c => <th key={c.key} className="px-3 py-2.5">{c.label}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {d.rows.data.map((r, ri) => (
                          <tr key={ri} className="border-b border-white/[0.04] text-[12px] transition-colors last:border-0 hover:bg-white/[0.03]">
                            {d.rows.columns.map(c => (
                              <td key={c.key} className="max-w-[200px] truncate px-3 py-2.5">{<Cellv col={c} row={r} />}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <p className="mt-4 text-right text-[9px] font-semibold uppercase tracking-widest text-muted">
                Computed {d.generatedAt ? new Date(d.generatedAt).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true }) : ''} · refreshes live
              </p>
            </>
          )}
        </div>
      </div>
      <style>{`
        @keyframes ddFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes ddRise { from { opacity: 0; transform: translateY(28px) scale(.985) } to { opacity: 1; transform: none } }
      `}</style>
    </div>
  );
};

/* small affordance chip pages put on cards whose charts drill down */
export const DrillHint = () => (
  <span className="hidden items-center gap-1 rounded-full border border-[rgba(229,192,107,.18)] bg-white/[0.03] px-2 py-0.5 text-[8.5px] font-black uppercase tracking-widest text-muted sm:inline-flex">
    <span className="material-symbols-outlined text-[11px] text-gold">left_click</span> Click to explore
  </span>
);

export default DrillDown;
